import { Controller } from '@hotwired/stimulus';

/**
 * Export Modal Controller
 *
 * Mounted on the async export progress modal (composed onto Ui::ModalComponent
 * via its `data:` forwarding).
 *
 * Mode stream (architecte) : opens the modal on connect + fires a reconcile,
 * then a SECOND one once the `<turbo-cable-stream-source>` is subscribed, then
 * the fallback loop below. The live broadcast remains the primary channel.
 *
 * Why two (PR #731, review #15): the connect reconcile leaves BEFORE the
 * subscription is confirmed, so it almost always reads pending/processing, and a
 * broadcast sent between that GET and the confirmation reached nobody — among
 * them the CR section 9 refresh of an annexed planning, which only the
 * `exports#show` catch-up replays. turbo-rails sets the `connected` attribute on
 * the source element in the ActionCable subscription's `connected` callback
 * (`cable_stream_source_element.js#subscriptionConnected`), i.e. once the server
 * has confirmed the subscription: from then on every broadcast is received, so
 * one reconcile at that instant closes the window. A MutationObserver watches
 * that attribute, fires once, and is disconnected right after (and on
 * `disconnect`). A reconnection re-setting `connected` does not fire again.
 *
 * Stream-mode fallback (R-8): a broadcast can still be lost after subscription
 * (cable drop, reconnection without replay), which left the modal stuck on
 * « Export en cours… » for a ready export. So stream mode ALSO runs the poll
 * tick every STREAM_FALLBACK_INTERVAL_MS until a terminal status lands, with
 * the same terminal stop and HTTP-failure ceiling as poll mode.
 *
 * Freshness guard (both modes): a reconcile response that left before the
 * terminal status may land after it. A non-terminal response is never rendered
 * over a terminal status already on screen; a terminal one still is.
 *
 * Mode poll (portails — spec export PDF §4.2/E13) : there is no cable (the
 * channel rejects connections without a Warden user) nor usable broadcast URLs
 * (the model broadcast embeds architect-only routes). So the reconcile runs in
 * a loop (POLL_INTERVAL_MS) against the surface's own showUrl until a terminal
 * status lands, signaled by `data-export-terminal="true"` on the status
 * container (the `_ready`/`_failed` partials, T9).
 *
 * Persistent-HTTP-failure ceiling: a permanently 404'ing showUrl (the export
 * got purged) must NOT poll a backgrounded tab forever. After
 * MAX_HTTP_FAILURES consecutive non-ok HTTP responses the loop stops. NETWORK
 * errors (`.catch`) are transient and do NOT count — they let the next tick
 * retry. A single ok response resets the counter.
 *
 * Transient-overlay cleanup: this modal is appended at runtime into the stable
 * `#export-modals` container. On `turbo:before-cache` it removes ITSELF (never
 * the container) so navigating away and back never restores a stale modal —
 * which would re-open it AND re-mount the `_ready` auto-download hook. Leaving
 * the container intact keeps it as a valid append target for a later re-export.
 *
 * Values:
 * - modalId: The modal element id (passed to window.openModal).
 * - showUrl: The export show URL OF THE SURFACE (turbo_stream reconcile target).
 * - poll: Boolean — cable-less reconcile loop every POLL_INTERVAL_MS (portals);
 *   false = stream mode, with the STREAM_FALLBACK_INTERVAL_MS fallback loop.
 * - statusId: dom_id of the status container (terminal detection).
 *
 * Targets:
 * - cableSource: the `<turbo-cable-stream-source>` (stream mode only; absent in
 *   poll mode) whose `connected` attribute triggers the second reconcile.
 * - status: the root of the `exports/_<status>` partial. Stimulus calls
 *   `statusTargetConnected` on the first paint AND after every replace
 *   (broadcast, reconcile, poll), whatever the path.
 *
 * Title (R-15): the modal title follows the export state (« Export en cours »,
 * « Export prêt », « Export impossible »). It lives in the modal header, OUTSIDE
 * the replaced status container, so each status partial carries its title in
 * `data-export-title` and `statusTargetConnected` copies it into the node the
 * dialog's `aria-labelledby` points to: the visible title and the accessible
 * name stay the same node. `textContent` only: never `innerHTML`.
 *
 * @example
 * <div data-controller="export-modal"
 *      data-export-modal-modal-id-value="modal_export_42"
 *      data-export-modal-show-url-value="/exports/42"
 *      data-export-modal-poll-value="false"
 *      data-export-modal-status-id-value="status_export_42">
 *   <turbo-cable-stream-source data-export-modal-target="cableSource" …></turbo-cable-stream-source>
 * </div>
 */
const POLL_INTERVAL_MS = 3000;
const STREAM_FALLBACK_INTERVAL_MS = 5000;
const MAX_HTTP_FAILURES = 5;
// Posé par la racine des partials `_ready` / `_failed`, tel que rendu par Rails.
const TERMINAL_MARKER = 'data-export-terminal="true"';
const SUBSCRIBED_ATTRIBUTE = 'connected';

export default class extends Controller {
  static targets = ['cableSource', 'status'];
  static values = { modalId: String, showUrl: String, poll: Boolean, statusId: String };

  connect() {
    this.httpFailures = 0;

    // Strip this transient modal from the page snapshot Turbo caches when the
    // user navigates away, so it is never restored (re-opened + re-mounting the
    // auto-download hook) on return. Removing only `this.element` (the modal)
    // leaves the parent `#export-modals` container as a valid re-export target.
    this.boundBeforeCache = () => this.element.remove();
    document.addEventListener('turbo:before-cache', this.boundBeforeCache);

    if (this.modalIdValue) {
      window.openModal(this.modalIdValue);
    }
    this.reconcile();
    if (this.pollValue) {
      this.timer = setInterval(() => this.pollTick(), POLL_INTERVAL_MS);
    } else {
      this.#reconcileOnceSubscribed();
      // R-8 : filet de rattrapage. Une diffusion perdue (câble coupé, reconnexion
      // sans rejeu) laissait la modale figée sur « Export en cours… » alors que
      // l'export était prêt. Même tour que le mode poll, arrêté au terminal.
      this.timer = setInterval(() => this.pollTick(), STREAM_FALLBACK_INTERVAL_MS);
    }
  }

  disconnect() {
    document.removeEventListener('turbo:before-cache', this.boundBeforeCache);
    this.stopPolling();
    this.#stopWatchingSubscription();
  }

  // R-15 : a new status partial landed (first paint, broadcast, reconcile or
  // poll) ⇒ the modal title follows it. A root without a title (stale cached
  // markup) leaves the title alone.
  statusTargetConnected(element) {
    const title = element.dataset.exportTitle;
    if (!title) return;
    const titleId = this.element.getAttribute('aria-labelledby');
    const heading = titleId ? document.getElementById(titleId) : null;
    if (heading) heading.textContent = title;
  }

  pollTick() {
    if (this.terminal()) {
      this.stopPolling();
      return;
    }
    if (this.httpFailures >= MAX_HTTP_FAILURES) {
      this.stopPolling();
      return;
    }
    this.reconcile();
  }

  terminal() {
    if (!this.hasStatusIdValue) return false;
    const el = document.getElementById(this.statusIdValue);
    return el?.dataset?.exportTerminal === 'true';
  }

  stopPolling() {
    if (this.timer) {
      clearInterval(this.timer);
      this.timer = null;
    }
  }

  // Stream mode: one more reconcile once the subscription is confirmed (see the
  // header). Already subscribed at connect ⇒ the connect reconcile left after
  // the confirmation, nothing to add.
  #reconcileOnceSubscribed() {
    if (!this.hasCableSourceTarget) return;
    const source = this.cableSourceTarget;
    if (source.hasAttribute(SUBSCRIBED_ATTRIBUTE)) return;

    this.subscriptionObserver = new MutationObserver(() => {
      if (!source.hasAttribute(SUBSCRIBED_ATTRIBUTE)) return;
      this.#stopWatchingSubscription();
      this.reconcile();
    });
    this.subscriptionObserver.observe(source, {
      attributes: true,
      attributeFilter: [SUBSCRIBED_ATTRIBUTE],
    });
  }

  #stopWatchingSubscription() {
    if (!this.subscriptionObserver) return;
    this.subscriptionObserver.disconnect();
    this.subscriptionObserver = null;
  }

  // Render the current server-side state into the modal body. In stream mode
  // this is the anti-race reconcile plus the fallback tick; in poll mode it is
  // the loop body.
  reconcile() {
    if (!this.hasShowUrlValue) return;

    fetch(this.showUrlValue, {
      headers: { Accept: 'text/vnd.turbo-stream.html' },
    })
      .then((response) => {
        if (!response.ok) {
          // Persistent HTTP failure (e.g. 404 — export purged). Count it so the
          // poll loop can give up after MAX_HTTP_FAILURES (see pollTick).
          this.httpFailures += 1;
          return null;
        }
        this.httpFailures = 0;
        return response.text();
      })
      .then((html) => {
        if (!html) return;
        // R-8 : une réponse partie avant l'état terminal peut arriver après lui
        // (diffusion « prêt » ou tour précédent plus rapide). Si l'écran est déjà
        // terminal et que la réponse ne l'est pas, on ne la rend pas : elle
        // ramènerait « Export en cours… » sur un export prêt, et plus aucun tour
        // ne la corrigerait. Une réponse terminale, elle, reste rendue : elle
        // porte le rattrapage des annexes du CR (#10b), et `auto-download` est
        // idempotent.
        if (this.terminal() && !html.includes(TERMINAL_MARKER)) return;
        window.Turbo.renderStreamMessage(html);
      })
      .catch(() => {
        // Best-effort on NETWORK error (transient): in stream mode the live
        // broadcast remains the primary update path; in both modes the next tick
        // retries. Network errors do NOT count toward the HTTP-failure ceiling.
      });
  }
}
